import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import {
  forwardRef,
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { PrimaryButton } from '../../../components/common/Buttons';
import { RadioButtonCheckedIcon } from '../../../components/Icon/RadioButtonCheckedIcon';
import { RadioButtonUncheckedIcon } from '../../../components/Icon/RadioButtonUncheckedIcon';
import { I18n } from '../../../i18n';
import { SlidePanel } from '../../../layouts/SlidePanel';
import { useAppDispatch, useAppSelector } from '../../../redux-rtk';
import {
  AppActions,
  HazardsSortMethod,
} from '../../../redux-rtk/slices/appSlice';
import { FocusTrap } from '@mui/base';
import { isReliableUserLocation } from '~/utils';

type SortOptionsPanelProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const HazardsSortPanel = forwardRef(
  ({ isOpen, onClose }: SortOptionsPanelProps, ref) => {
    const selectedValue = useAppSelector(
      state => state.app.data.selectedHazardSort || HazardsSortMethod.Date,
    );
    const currentLocation = useAppSelector(
      state => state.user.data.currentLocation,
    );
    const [pendingValue, setPendingValue] =
      useState<HazardsSortMethod>(selectedValue);
    const dispatch = useAppDispatch();

    const applyHandler = () => {
      dispatch(AppActions.selectHazardSort(pendingValue));
      onClose();
    };

    const onChangeHandler = (event: SyntheticEvent) => {
      setPendingValue(
        (event.target as HTMLInputElement).value as HazardsSortMethod,
      );
    };

    // If other parts of the app (e.g., the logic for parsing URL params) updates the sort method, we also want to update the Pending value in this component
    useEffect(() => {
      setPendingValue(selectedValue);
    }, [selectedValue]);

    const radioRefs = useRef<(HTMLInputElement | null)[]>([]);
    const focusedElementRef = useRef<HTMLElement | null>(null);
    const handleKeydown = useCallback(event => {
      if (event.srcElement.name === 'hazard-sort-options') {
        const isArrowDownKeyPressed =
          event.key === 'ArrowDown' || event.keyCode === 40;
        const isArrowUpKeyPressed =
          event.key === 'ArrowUp' || event.keyCode === 38;
        if (isArrowDownKeyPressed || isArrowUpKeyPressed) {
          let sortOptions = document.getElementsByName(event.srcElement.name);
          const currentIndex = Array.prototype.indexOf.call(
            sortOptions,
            event.srcElement,
          );
          let nextFocussable: HTMLElement;
          if (isArrowDownKeyPressed) {
            nextFocussable = sortOptions[currentIndex + 1] || sortOptions[0];
          } else {
            nextFocussable =
              sortOptions[currentIndex - 1] ||
              sortOptions[sortOptions.length - 1];
          }
          focusedElementRef.current = nextFocussable;
          focusedElementRef.current?.focus();
          //focus directly to the radio buttons without triggering re-render
          const nextRadioValue = (focusedElementRef.current as HTMLInputElement)
            ?.value;
          if (nextRadioValue) {
            setPendingValue(nextRadioValue as HazardsSortMethod);
          }
          event.preventDefault();
        }
      }
    }, []);

    useEffect(() => {
      document.addEventListener('keydown', handleKeydown);
      return () => {
        document.removeEventListener('keydown', handleKeydown);
      };
    }, []);

    useEffect(() => {
      radioRefs.current.find(radio => radio?.value === pendingValue)?.focus?.();
    }, [pendingValue]); // radio button focus after state update

    interface RadioItemProps {
      method: HazardsSortMethod;
      label: string;
      checked: boolean;
      children?: React.ReactNode;
      index: number; // index to track each radio button
      disabled?: boolean;
    }

    const RadioItem = ({
      method,
      label,
      checked,
      children,
      index,
      disabled = false,
    }: RadioItemProps) => {
      return (
        <Box sx={{ marginBottom: 2 }}>
          <FormControlLabel
            value={method}
            label={
              <>
                <Typography variant="body1">{label}</Typography>
                {children}
              </>
            }
            control={
              <Radio
                inputRef={element => (radioRefs.current[index] = element)}
                icon={<RadioButtonUncheckedIcon />}
                checkedIcon={<RadioButtonCheckedIcon />}
                checked={checked}
                disabled={disabled}
              />
            }
            labelPlacement="start"
            sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
          />
        </Box>
      );
    };

    return (
      <FocusTrap open={isOpen}>
        <SlidePanel
          isOpen={isOpen}
          onClose={onClose}
          backButtonText={I18n.t('fires.sortPanelBackText')}
          ref={ref}
        >
          <Typography variant="h3" sx={{ mt: 2 }}>
            {I18n.t('fires.sortPanelTitle')}
          </Typography>
          <FormControl
            sx={{
              mt: 2,
              mb: 2,
              p: 2,
              backgroundColor: 'grey.50',
              borderRadius: 1,
            }}
            variant="standard"
          >
            <RadioGroup
              aria-labelledby="hazard-sort-options"
              name="hazard-sort-options"
              onChange={onChangeHandler}
            >
              <RadioItem
                index={0}
                method={HazardsSortMethod.Distance}
                label={I18n.t('fires.sortByDistance')}
                checked={pendingValue === HazardsSortMethod.Distance}
                disabled={!isReliableUserLocation(currentLocation)}
              >
                <Typography variant="caption">
                  {I18n.t('fires.sortByDistanceDesc')}
                </Typography>
              </RadioItem>
              <RadioItem
                index={1}
                method={HazardsSortMethod.Name}
                label={I18n.t('fires.sortByName')}
                checked={pendingValue === HazardsSortMethod.Name}
              >
                <Typography variant="caption">
                  {I18n.t('fires.sortByNameDesc')}
                </Typography>
              </RadioItem>
              <RadioItem
                index={2}
                method={HazardsSortMethod.Date}
                label={I18n.t('fires.sortByDate')}
                checked={pendingValue === HazardsSortMethod.Date}
              >
                <Typography variant="caption">
                  {I18n.t('fires.sortByDateDesc')}
                </Typography>
              </RadioItem>
            </RadioGroup>
          </FormControl>

          <PrimaryButton
            sx={{ width: '100%' }}
            label={I18n.t('fires.sortApply')}
            onClick={applyHandler}
          />
        </SlidePanel>
      </FocusTrap>
    );
  },
);
